import { useEffect, useRef } from "react";

/**
 * Calendly inline widget.
 * Loads the Calendly external script once and renders the booking iframe
 * inline. Fully re-renders whenever the `url` prop changes (used to swap
 * between 30-min / 60-min / 120-min booking pages).
 */
export default function CalendlyInline({
  url = "https://calendly.com/camili_eyes",
  minWidth = "320px",
  height = 700,
  className = "",
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
    const container = containerRef.current;
    if (!container) return;

    // Clear any previously rendered widget for this container so switching
    // between session URLs paints the new booking page cleanly.
    container.innerHTML = "";

    const render = () => {
      if (window.Calendly && container) {
        try {
          window.Calendly.initInlineWidget({
            url,
            parentElement: container,
          });
        } catch (e) {
          // no-op — Calendly may also auto-render via CSS class
        }
      }
    };

    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing && window.Calendly) {
      render();
    } else if (existing) {
      existing.addEventListener("load", render, { once: true });
    } else {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.addEventListener("load", render, { once: true });
      document.body.appendChild(s);
    }
  }, [url]);

  return (
    <div
      ref={containerRef}
      data-testid="calendly-inline-widget"
      className={`calendly-inline-widget ${className}`}
      data-url={url}
      style={{ minWidth, height: `${height}px` }}
    />
  );
}
